import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getLogisticsMilestoneNextAction } from "../../utils/logisticsMilestoneDetails";
import { getLogisticsMilestoneProgressPercent } from "../../utils/logisticsStageUpdateOptions";
import { getLogisticsStageConfig } from "../../utils/logisticsStages";

export const getStageHeatAlpha = (count, maxCount) => {
  if (!maxCount) return 0.06;
  return Number((0.08 + (count / maxCount) * 0.28).toFixed(2));
};

const formatFallbackStageLabel = (stageKey) =>
  String(stageKey || "")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const getStageLabel = (stageKey) => {
  const stageConfig = getLogisticsStageConfig(stageKey);

  if (
    stageKey &&
    stageKey !== "NOT_STARTED" &&
    stageConfig.label === "Not Started"
  ) {
    return formatFallbackStageLabel(stageKey);
  }

  return stageConfig.label;
};

// Small uppercase label used above field values, matching the
// "File Name" / "File Type" style captions in the reference panel.
export const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: "text.secondary",
      lineHeight: 1.4,
    }}
  >
    {children}
  </Typography>
);

// Shared stage/heat-map row list used by both the compact Insights side
// panel and the Insights dashboard, so the two surfaces render identical
// stage breakdowns from one implementation.
const LogisticsStageBreakdownList = ({ stages = [] }) => {
  const maxStageCount = Math.max(
    0,
    ...stages.map((stage) => Number(stage.count || 0)),
  );

  return (
    <Stack spacing={0.85}>
      {stages.map((stage, index) => {
        const count = Number(stage.count || 0);
        const milestonePercent = getLogisticsMilestoneProgressPercent(
          stage.stage,
          stage.milestone,
        );
        const nextAction = getLogisticsMilestoneNextAction(
          stage.stage,
          stage.milestone,
        );

        return (
          <Box key={`${stage.stage}-${stage.milestone || "none"}-${index}`}>
            <Box
              data-testid="logistics-insights-stage-row"
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 1.5,
                alignItems: "center",
                minHeight: 36,
                px: { xs: 1, sm: 1.25 },
                borderRadius: 2,
                backgroundColor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    getStageHeatAlpha(count, maxStageCount),
                  ),
              }}
            >
              <Typography
                sx={{
                  minWidth: 0,
                  fontSize: 13,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getStageLabel(stage.stage)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "text.primary",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                }}
              >
                {count} ({milestonePercent}%)
              </Typography>
            </Box>

            <Box sx={{ mt: 0.75 }}>
              <FieldLabel>Next Action</FieldLabel>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {nextAction}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default LogisticsStageBreakdownList;
